import axios from "axios";
import moment from "moment";
import { IMedia } from "@/models";
import { IComments } from "../models/course";
import { ICourseReceipt, IStoreReceipt } from "../models/receipt";
import { ICertificate } from "../models/certificate";
import { server as $axios } from "./server.ts";
import { IProfile } from "@/models/profile";

export const login = async (payload: string) => {
  return $axios.post("/hosted_courses/customer_hub/login", payload, {
    headers: { "content-type": "application/x-www-form-urlencoded" },
  });
};

export const verifyLogin = async (payload: string) => {
  return $axios.post("/hosted_courses/customer_hub/verify", payload, {
    headers: { "content-type": "application/x-www-form-urlencoded" },
  });
};

interface FetchProfileParams {
  page?: number;
  limit?: number;
  product_id?: string;
  needle?: string;
  filter?: Record<string, any>;
  dateRange?: {
    from: string;
    to: string;
  };
}

export const fetchProfile = async (
  params: FetchProfileParams
): Promise<IProfile> => {
  const {
    page = 1,
    limit = 10,
    needle,
    filter,
    dateRange,
    product_id = "",
  } = params;

  const searchParams = new URLSearchParams();

  searchParams.append("page", page.toString());
  searchParams.append("limit", limit.toString());
  if (needle) searchParams.append("needle", needle);
  if (product_id) searchParams.append("product_id", product_id);
  if (filter) {
    Object.entries(filter).forEach(([key, value]) => {
      searchParams.append(key, value as string);
    });
  }
  if (dateRange?.from) {
    searchParams.append(
      "from",
      moment(dateRange?.from, "DD MMM YYYY").toISOString()
    );
    searchParams.append(
      "to",
      moment(dateRange?.to, "DD MMM YYYY").toISOString()
    );
  }

  const { data } = await $axios.get<IProfile>(
    `/hosted_courses/customer_hub?${searchParams}`
  );
  return data;
};

export const fetchReceipt = async ({
  reference,
  receiptType,
}: {
  reference?: string;
  receiptType?: string | undefined;
}): Promise<ICourseReceipt | IStoreReceipt> => {
  const url =
    receiptType === "hosted_courses"
      ? `${
          import.meta.env.REACT_APP_BASE_URL
        }/hosted_courses/courses/purchase_receipt?reference=${reference}`
      : `${
          import.meta.env.REACT_APP_BASE_URL
        }/stores/products/receipts?reference=${reference}`;

  try {
    const { data } = await axios.get(`${url}`);
    return data;
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export const downloadStoreProduct = async (
  productId: string,
  ref: string
): Promise<IMedia> => {
  try {
    const { data } = await axios.get(
      `${
        import.meta.env.REACT_APP_BASE_URL
      }/stores/products/${productId}/download?reference=${ref}`
    );
    return data;
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export const updateCardDetails = async (
  productId: string,
  reference: string
): Promise<{ url: string }> => {
  try {
    const { data } = await $axios.get(
      `/hosted_courses/customer_hub/update-card?product_id=${productId}&reference=${reference}`
    );
    return { ...data, message: "Card details updated successfully" };
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export async function fetchComments(payload: {
  lectureId: string;
  page: number;
  limit: number;
}): Promise<IComments> {
  try {
    const { lectureId, page, limit } = payload;

    const { data } = await $axios.get(
      `/hosted_courses/comments/customerhub_lecture_comments?lecture_id=${lectureId}&page=${
        page || 1
      }&limit=${limit || 6}`
    );
    return data;
  } catch (e) {
    throw new Error((e as Error).message);
  }
}

export async function addComment(payload: {
  lecture_id: string;
  comment: string;
}) {
  try {
    const { data } = await $axios.post(
      "/hosted_courses/comments/customerhub_comment",
      payload
    );
    return data;
  } catch (e) {
    throw new Error((e as Error).message);
  }
}

export async function addCommentReply(payload: {
  comment_id: string;
  comment: string;
}) {
  try {
    const { data } = await $axios.post(
      "/hosted_courses/comments/customerhub_reply",
      payload
    );
    return data;
  } catch (e) {
    throw new Error((e as Error).message);
  }
}

export async function convertToPng(image: string) {
  try {
    const { data } = await $axios.post(
      "/hosted_courses/customer_hub/webp2png",
      {
        path: image,
      }
    );
    return data;
  } catch (e) {
    throw new Error((e as Error).message);
  }
}

export async function fetchCertificate(
  certificateId: string
): Promise<ICertificate[]> {
  try {
    const { data } = await $axios.get(
      `/hosted_courses/customer_hub/public_cert?certificate_id=${certificateId}`
    );
    return data;
  } catch (e) {
    throw new Error((e as Error).message);
  }
}
